import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ITreeNodeProps, TreeNode } from "@/Common/Components/Collections/TreeNode";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { TreeNodeTarget } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { OutboundFaxCreationPopup } from "@/Documents/Components/OutboundFax/OutboundFaxCreationPopup";
import { AssignedDocumentsMenuItemsRegistryView } from "@/Documents/Extensions/AssignedDocumentsMenuItemsRegistry";
import { SelectedDocumentView } from "@/Documents/Models/UI/IDocumentsTabModel";
import { InboundFaxesMailboxActions } from "../InboundFax/InboundFaxesMailboxActions";

import "./DocumentTreeControl.css";

interface IDocumentTreeControlProps {
    DocumentClasses: Immutable.List<DocumentClassModel>;
    NonApprovedDocumentCount: number;
    isDeactivatedAccountEnabled?: boolean;
    selectedView: SelectedDocumentView;
    isUsedInGlobalDocumentTab: boolean;
}

class DocumentTreeControlState {
    public showOutboundFaxCreationPopup: boolean;

    public static getDefault(): DocumentTreeControlState {
        let s = new DocumentTreeControlState();
        s.showOutboundFaxCreationPopup = false;
        return s;
    }
}

class DocumentTreeControl extends BaseReactComponent<IDocumentTreeControlProps, DocumentTreeControlState> {
    constructor(props: IDocumentTreeControlProps) {
        super(props);

        this.state = DocumentTreeControlState.getDefault();
    }

    public render(): JSX.Element {
        let treeNodeArray = [];
        if (this.props.DocumentClasses) {
            treeNodeArray = this.props.DocumentClasses
                .map(item => this.convertToTreeNode(item))
                .toArray();
        }

        let docCount = 0;
        if (this.props.NonApprovedDocumentCount)
            docCount = this.props.NonApprovedDocumentCount;

        const showInboundFaxes = sessionStore.canReceiveFaxes() && this.props.isUsedInGlobalDocumentTab;

        return (
            <div className="document-tree-control-component" data-cy="document-treeMenu">
                <ul className="accordion" data-accordion="">

                    <li className="accordion-navigation clearfix">
                        {cacheStore?.ClinicSettings.EnableSendFax && (
                            <div className="displaced-send-fax-btn">
                                <a onClick={e => this.OnCreateNewFaxClick(e)} data-cy="fax-createNewIcon">
                                    <span className="plus_icon" />
                                    <span className="navigation-send-fax-label">{cacheStore.GetString("SendFaxPopupLabel")}</span>
                                </a>
                            </div>
                        )}
                        <div className="displaced-nav-btns">
                            {showInboundFaxes && (
                                <div>
                                    <h4 className="bold received-fax-title">{cacheStore.GetString("ReceivedFaxesLabel")}</h4>
                                    <div className="displaced">
                                        <div className="pull-left">
                                            <div>
                                                <a
                                                    className={this.props.selectedView === "faxInbox" ? "text-underline" : ""}
                                                    data-cy="document-faxNew"
                                                    onClick={e => this.OnFaxesClick(e)}
                                                >
                                                    {this.getInboundFaxTitleWithUnmanagedFaxCount()}
                                                </a>
                                            </div>
                                            <div>
                                                <a
                                                    className={this.props.selectedView === "faxTrash" ? "text-underline" : ""}
                                                    onClick={e => this.OnTrashClick(e)}
                                                >
                                                    {cacheStore.GetString("TrashLabel")}
                                                </a>
                                            </div>
                                            <div>
                                                <a
                                                    className={this.props.selectedView === "faxIndexed" ? "text-underline" : ""}
                                                    data-cy="document-faxIndexed"
                                                    onClick={e => this.OnIndexedClick(e)}
                                                >
                                                    {cacheStore.GetString("IndexedLabel")}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {showInboundFaxes &&
                                <h4 className="bold assigned-fax-title">{cacheStore.GetString("FaxesAssignedLabel")}</h4>
                            }

                            <a onClick={e => this.OnCreateNewDocumentClick(e)} className="plus_icon pull-left" data-cy="document-createNewIcon">&nbsp; </a>
                            <div className="pull-left">
                                <div>
                                    <a className={this.props.selectedView === "allDocuments" ? "text-underline" : ""} onClick={e => this.OnAllDocumentsClick(e)} data-cy="document-allDocuments">
                                        {cacheStore.GetString("AllDocumentsLabel")} ({docCount})
                                    </a>
                                </div>
                                <div>
                                    <a className={this.props.selectedView === "orphan" ? "text-underline" : ""} onClick={e => this.OnOrphansClick(e)} data-cy="document-orphan">
                                        {cacheStore.GetString("OrphansLabel")}
                                    </a>
                                </div>
                                {this.props.isDeactivatedAccountEnabled && (
                                    <div>
                                        <a className={this.props.selectedView === "deactivatedAccount" ? "text-underline" : ""} onClick={e => this.OnDeactivatedAccountClick(e)} data-cy="document-deactivatedAccounts">
                                            {cacheStore.GetString("DeactivatedAccountsLabel")}
                                        </a>
                                    </div>
                                )}
                                <div>
                                    <AssignedDocumentsMenuItemsRegistryView
                                        selectedView={this.props.selectedView}
                                        navigateToExtension={(extensionId: string) => {
                                        // For now we simply pass through the extensionId (enum key string) as the extensionName payload.
                                        // The previous hard-coded string "HealthReportManagerOrphans" never matched the real id
                                        // ("OntarioMdHealthReportManager_OrphansView"), so no dispatch occurred.
                                            LocalEvent.Dispatch(LocalOperations.Documents.Tree_Load_AssignedDocuments_Extension, { extensionName: extensionId });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </li>
                    {treeNodeArray.map(treeNode => {
                        if (treeNode.props.componentId == null)
                            return null;
                        else
                            return (
                                <TreeNode
                                    lookupId={treeNode.props.lookupId}
                                    key={treeNode.props.componentId}
                                    componentId={treeNode.props.componentId}
                                    className={treeNode.props.className}
                                    childNodes={treeNode.props.childNodes}
                                    isExpanded={treeNode.props.isExpanded}
                                    isSelected={treeNode.props.isSelected}
                                    text={treeNode.props.text}
                                    childClickTarget={TreeNodeTarget.Myle_Documents_DocumentTree_SubClassSelection}
                                    parentClickOperation={LocalOperations.Documents.Tree_Toggle_Parent}
                                />
                            );
                    })}
                </ul>

                {this.state.showOutboundFaxCreationPopup && (
                    <OutboundFaxCreationPopup
                        onCancel={() => this.OnCloseNewFaxPopup()}
                        onSubmit={(faxDetails, onCompleted) => InboundFaxesMailboxActions.sendFax(faxDetails, onCompleted, () => this.OnCloseNewFaxPopup())}
                        popupTitle={cacheStore.GetString("SendFaxPopupLabel")}
                    />
                )}
            </div>
        );
    }

    private convertToTreeNode(docClass: DocumentClassModel): TreeNode {
        const getBaseProperties = (docClass: DocumentClassModel): ITreeNodeProps => ({
            isExpanded: docClass.IsExpanded,
            isSelected: docClass.IsSelected ? docClass.IsSelected : false,
            text: docClass.Name,
            componentId: docClass.LookupCode,
            referencedObject: docClass,
            lookupId: docClass.LookupId
        });

        const docClassTreeNode = getBaseProperties(docClass);
        docClassTreeNode.className = "accordion-navigation leftNavAccordion expandable";

        if (docClass.IsExpanded)
            docClassTreeNode.className += " active";

        if (docClass.SubClasses?.count()) {
            const subclassNodes = docClass.SubClasses.map(item => this.convertToTreeNode(item));

            docClassTreeNode.childNodes = Immutable.fromJS(subclassNodes);
        }

        return new TreeNode(docClassTreeNode);
    }

    private OnAllDocumentsClick(e: React.MouseEvent): void {
        e.preventDefault();
        sessionStore.dispatch(LocalOperations.Documents.Tree_All_Documents_Selected);
        sessionStore.dispatch(LocalOperations.Documents.Tree_SubClass_Selection, { IsAllDocumentSet: true });
        sessionStore.dispatch(LocalOperations.Documents.Tree_ClearSelection);
    }

    private OnOrphansClick(e: React.MouseEvent): void {
        e.preventDefault();
        LocalEvent.Dispatch(LocalOperations.Documents.Tree_Load_Orphans);
    }

    private OnIndexedClick(e: React.MouseEvent): void {
        e.preventDefault();
        LocalEvent.Dispatch(LocalOperations.Documents.Tree_Load_Fax_Indexed);
    }

    private OnTrashClick(e: React.MouseEvent): void {
        e.preventDefault();
        LocalEvent.Dispatch(LocalOperations.Documents.Tree_Load_Fax_Trash);
    }

    private OnFaxesClick(e: React.MouseEvent): void {
        e.preventDefault();
        LocalEvent.Dispatch(LocalOperations.Documents.Tree_Load_Fax_Inbox);
    }

    private OnDeactivatedAccountClick(e: React.MouseEvent): void {
        e.preventDefault();
        LocalEvent.Dispatch(LocalOperations.Documents.Tree_Load_DeactivatedAccount);
    }

    private OnCreateNewDocumentClick(e: React.MouseEvent): void {
        e.preventDefault();
        LocalEvent.Dispatch(LocalOperations.Documents.Show_Create_Document);
    }

    private OnCreateNewFaxClick(e: React.MouseEvent): void {
        e.preventDefault();
        this.mutateState(s => s.showOutboundFaxCreationPopup = true);
    }

    private OnCloseNewFaxPopup(): void {
        this.mutateState(s => s.showOutboundFaxCreationPopup = false);
    }

    private getInboundFaxTitleWithUnmanagedFaxCount(): string {
        const counter = documentsStore.InboundFaxInboxModel.queryRowCount;
        const unmanagedCount = counter
            ? ` (${counter})`
            : "";

        return `${cacheStore.GetString("NewLabel")}${unmanagedCount}`;
    }
}

export { DocumentTreeControl };
